import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import { useCallback, useMemo, useState } from 'react';
import { QueryFactory } from 'services/api';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { Field } from 'components/uikit/Field.tsx';
import { HookFormStyledDatePicker } from 'components/uikit/datePicker/HookFormDatePicker.tsx';
import styles from './BulkUpdateDateModal.module.scss';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { useSelectModeContext } from '../../useSelectModeContext.tsx';
import {
  DocumentListItemDto,
  SystemVariableType,
  VariableListItemDto,
} from 'services/api/api-client.types.ts';
import { convertDateToString } from 'helpers/date-helpers.ts';
import { isValid } from 'date-fns';
import { FileNodeState } from '../../SelectionStore.ts';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import { useVariablesContext } from 'pages/authorized/variables/variablesContext/VariablesContext.tsx';

type BulkUpdateDateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  revisionId: number;
  revisionDocuments: DocumentListItemDto[];
  revisionVariables: VariableListItemDto[];
  selectedDocuments: FileNodeState[];
};

export const BulkUpdateDateModal = (props: BulkUpdateDateModalProps) => {
  const queryClient = useQueryClient();
  const i18n = useScopedTranslation('Component.BulkUpdateDateModal');
  const i18nUiKit = useScopedTranslation('uikit.inputs');
  const { setIsSelectMode } = useSelectModeContext();
  const { refreshVariables } = useVariablesContext();

  const [isLoading, setIsLoading] = useState(false);
  const defaultValues = useMemo(() => ({ date: new Date() }), []);

  const form = useAdvancedForm<{ date: Date }>(
    useCallback(
      async (data) => {
        setIsLoading(true);

        const convertedDate = convertDateToString({
          date: data.date,
          divider: '.',
          mirrorOrder: true,
        });

        const dateVarsIds = getDateVariablesIdsFromSelectedDocuments(
          props.selectedDocuments.map((d) => d.documentId),
        );

        try {
          await QueryFactory.VariableClient.bulkPatch(
            dateVarsIds.map((id) => ({
              id,
              revisionId: props.revisionId,
              patchDto: { actualValue: convertedDate },
            })),
          );
        } catch (e) {
          showErrorToast(e);
        }

        await onModalClose();

        setIsSelectMode(false);
        setIsLoading(false);
      },
      [
        props.revisionId,
        props.revisionVariables,
        props.revisionDocuments,
        props.selectedDocuments,
      ],
    ),
    { defaultValues },
  );

  const onModalClose = async () => {
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
    });
    await refreshVariables();

    form.reset();
    form.clearErrors();
    props.onClose();
  };

  const getDateVariablesIdsFromSelectedDocuments = (
    selectedDocumentsIds: number[],
  ) => {
    const initialIdByDocumentId = new Map(
      props.revisionDocuments.map((rd) => [rd.id, rd.initialId]),
    );

    const dateVarIdByBasedOnDocumentId = new Map(
      props.revisionVariables
        .filter((v) => v.systemVariableType === SystemVariableType.Date)
        .map((v) => [v.basedOnDocument?.id, v.id]),
    );

    return selectedDocumentsIds.flatMap((selectedId) => {
      const initialId = initialIdByDocumentId.get(selectedId);
      if (initialId == null) return [];

      const varId = dateVarIdByBasedOnDocumentId.get(initialId);

      return varId != null ? [varId] : [];
    });
  };

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onModalClose}
      isBlocking
      title={i18n.t('title')}
    >
      <Loading loading={isLoading}>
        <form onSubmit={form.handleSubmitDefault}>
          <Field title={i18n.t('date_field')} className={styles.field}>
            <HookFormStyledDatePicker<{ date: Date }>
              control={form.control}
              name="date"
              rules={{
                validate: (v) => {
                  if (v == null) return i18nUiKit.t('required');
                  if (!isValid(v)) return i18n.t('errors.invalid_date');
                  return true;
                },
              }}
              errorText={form.formState.errors.date?.message}
              classes={{ textField: styles.dateTextField }}
            />
          </Field>
          <div className={styles.footer}>
            <Button
              title={i18n.t('cancel_button')}
              className={styles.button}
              color={ButtonColor.Secondary}
              onClick={onModalClose}
            />
            <Button
              title={i18n.t('ok_button')}
              className={styles.button}
              type={'submit'}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};
